import { useCallback, useContext, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import csvDownload from 'json-to-csv-export';
import { useClientUser } from '@/hooks';
import { UserContext } from '@/contexts/UserContext';
import {
    QUERY_CLIENTS,
    QUERY_DELIVERY_PROFILES,
    UPSERT_DELIVERY_PROFILE,
    GET_DELIVERY_PROFILE,
    CREATE_SUBREGION,
    UPSERT_CARRIER_MAPPING,
    UPLOAD_SUBREGIONS,
    QUERY_DELIVERY_PROFILE_ASSIGNMENTS,
    APPLY_DELIVERY_PROFILE_ASSIGNMENTS,
} from './graphql';
import { DELETE_SUBREGION, INSERT_ZIP_MAPPINGS, DELETE_ZIP_MAPPINGS, UPDATE_SUBREGION } from '../graphql/mutations';
import { GET_CORP_PRICING_OVERRIDES } from '@/components/CorporateUsers/graphql';
import {
    buildAssignmentRows,
    filterCarriersByLeg,
    parseImportedSubregions,
    serializeSubregionsForExport,
} from './utils';
import { useColumns } from './constants';

export const useRoleScope = () => {
    const { isOnwardAdmin, isCorporateAdmin, organization_id } = useClientUser();
    return useMemo(() => {
        if (isOnwardAdmin) return { kind: 'onward' };
        if (isCorporateAdmin) return { kind: 'corp', org_id: organization_id };
        return { kind: 'none' };
    }, [isOnwardAdmin, isCorporateAdmin, organization_id]);
};

const whereForScope = (scope) => {
    if (scope.kind === 'onward') return { organization_id: { _is_null: true } };
    if (scope.kind === 'corp') return { organization_id: { _eq: scope.org_id } };
    return null;
};

export const useDeliveryProfileSelection = (scope, { onError } = {}) => {
    const [deliveryProfileId, selectDeliveryProfile] = useState(null);
    const where = whereForScope(scope);
    const skip = !where || (scope.kind === 'corp' && !scope.org_id);

    const { data: profilesData, loading: profilesLoading } = useQuery(QUERY_DELIVERY_PROFILES, {
        variables: { where: where || {} },
        skip,
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load delivery profiles');
        },
    });

    const { data: profileData, loading: profileLoading } = useQuery(GET_DELIVERY_PROFILE, {
        variables: { delivery_profile_id: deliveryProfileId },
        skip: !deliveryProfileId,
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load delivery profile');
        },
    });

    const [upsertMutation] = useMutation(UPSERT_DELIVERY_PROFILE, {
        onError: (e) => {
            captureException(e);
            onError?.('Failed to save delivery profile');
        },
        update: (cache, { data: { result } }) => {
            cache.updateQuery({ query: QUERY_DELIVERY_PROFILES, variables: { where } }, (data) => {
                const list = data?.results || [];
                const idx = list.findIndex((p) => p.delivery_profile_id === result.delivery_profile_id);
                if (idx === -1) return { ...(data || {}), results: [...list, result] };
                const next = [...list];
                next[idx] = { ...next[idx], ...result };
                return { ...(data || {}), results: next };
            });
        },
        onCompleted: ({ result }) => selectDeliveryProfile(result.delivery_profile_id),
    });

    const upsertProfile = useCallback(
        ({ delivery_profile_id, name }) => {
            const base = { name };
            const delivery_profile = delivery_profile_id
                ? { delivery_profile_id, ...base }
                : scope.kind === 'corp'
                ? { ...base, organization_id: scope.org_id }
                : base;
            return upsertMutation({ variables: { delivery_profile } });
        },
        [scope.kind, scope.org_id, upsertMutation]
    );

    return {
        deliveryProfileId,
        deliveryProfile: profileData?.result || null,
        deliveryProfiles: profilesData?.results || [],
        selectDeliveryProfile,
        upsertProfile,
        loading: profilesLoading || profileLoading,
    };
};

export const useProfileSubregionsMutations = (deliveryProfileId, { onError } = {}) => {
    const cacheVars = { delivery_profile_id: deliveryProfileId };

    const onMutationError = (label) => (e) => {
        captureException(e);
        onError?.(`Failed to ${label}`);
    };

    const [insertSubregion, { loading: insertSubregionLoading }] = useMutation(CREATE_SUBREGION, {
        onError: onMutationError('create subregion'),
        update: (cache, { data: { result } }) => {
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: [...(data?.result?.subregions || []), result],
                },
            }));
        },
    });

    const [patchSubregion, { loading: updateSubregionLoading }] = useMutation(UPDATE_SUBREGION, {
        onError: onMutationError('update subregion'),
    });

    const [removeSubregion, { loading: deleteSubregionLoading }] = useMutation(DELETE_SUBREGION, {
        onError: onMutationError('delete subregion'),
        update: (cache, { data: { removed } }) => {
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: (data?.result?.subregions || []).filter((s) => s.subregion_id !== removed.subregion_id),
                },
            }));
        },
    });

    const createSubregion = useCallback(
        (subregion) =>
            insertSubregion({
                variables: { subregion: { ...subregion, delivery_profile_id: deliveryProfileId } },
            }),
        [deliveryProfileId, insertSubregion]
    );

    const updateSubregion = useCallback(
        (subregion) => {
            const { subregion_id, __typename, zips, carriers, ...rest } = subregion;
            return patchSubregion({
                variables: {
                    subregion_id,
                    update: rest,
                },
            });
        },
        [patchSubregion]
    );

    const deleteSubregion = useCallback(
        (subregion) => removeSubregion({ variables: { subregion_id: subregion.subregion_id } }),
        [removeSubregion]
    );

    const [insertZipsMutation, { loading: insertZipsLoading }] = useMutation(INSERT_ZIP_MAPPINGS, {
        onError: onMutationError('add zips'),
        update: (cache, { data: { created } }) => {
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: (data?.result?.subregions || []).map((s) =>
                        s.subregion_id === created.returning[0].subregion_id
                            ? { ...s, zips: [...s.zips, ...created.returning] }
                            : s
                    ),
                },
            }));
        },
    });

    const [deleteZipsMutation, { loading: deleteZipsLoading }] = useMutation(DELETE_ZIP_MAPPINGS, {
        onError: onMutationError('remove zips'),
        update: (cache, { data: { removed } }) => {
            const removedSet = (removed.returning || []).reduce((acc, z) => {
                acc[z.subregion_id] = { ...(acc[z.subregion_id] || {}), [z.zip]: true };
                return acc;
            }, {});
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: (data?.result?.subregions || []).map((s) => ({
                        ...s,
                        zips: s.zips.filter((z) => !removedSet[s.subregion_id]?.[z.zip]),
                    })),
                },
            }));
        },
    });

    const [upsertMappingMutation, { loading: upsertMappingLoading }] = useMutation(UPSERT_CARRIER_MAPPING, {
        onError: onMutationError('upsert carrier mapping'),
        update: (cache, { data: { result } }) => {
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: (data?.result?.subregions || []).map((s) => {
                        if (s.subregion_id !== result.subregion_id) return s;
                        const carriers = [...(s.carriers || [])];
                        const idx = carriers.findIndex((c) => c.mapping_id === result.mapping_id);
                        if (idx !== -1) carriers[idx] = result;
                        else carriers.push(result);
                        carriers.sort((a) => (a.is_primary ? -1 : 0));
                        return { ...s, carriers };
                    }),
                },
            }));
        },
    });

    const [uploadMutation, { loading: uploadLoading }] = useMutation(UPLOAD_SUBREGIONS, {
        onError: onMutationError('upload subregions'),
        update: (cache, { data: { deleted, created } }) => {
            const removedIds = (deleted.returning || []).map((r) => r.subregion_id);
            cache.updateQuery({ query: GET_DELIVERY_PROFILE, variables: cacheVars }, (data) => ({
                ...(data || {}),
                result: {
                    ...(data?.result || {}),
                    subregions: [
                        ...(data?.result?.subregions || []).filter((s) => !removedIds.includes(s.subregion_id)),
                        ...(created.returning || []),
                    ],
                },
            }));
        },
    });

    const addZips = useCallback((mappings) => insertZipsMutation({ variables: { mappings } }), [insertZipsMutation]);
    const removeZips = useCallback(
        (mappings) => deleteZipsMutation({ variables: { mappings: mappings.map((m) => m.mapping_id) } }),
        [deleteZipsMutation]
    );
    const removeZip = useCallback(
        (mapping) => deleteZipsMutation({ variables: { mappings: [mapping.mapping_id] } }),
        [deleteZipsMutation]
    );
    const addCarrierMapping = useCallback(
        ({ __typename, ...mapping }) => upsertMappingMutation({ variables: { mapping } }),
        [upsertMappingMutation]
    );
    const uploadSubregions = useCallback(
        (subregions, to_delete) => uploadMutation({ variables: { subregions, to_delete } }),
        [uploadMutation]
    );

    return {
        createSubregion,
        updateSubregion,
        deleteSubregion,
        addZips,
        removeZips,
        removeZip,
        addCarrierMapping,
        uploadSubregions,
        loading: {
            subregions: insertSubregionLoading || deleteSubregionLoading || uploadLoading,
            zips: insertZipsLoading || deleteZipsLoading,
            carriers: upsertMappingLoading,
        },
    };
};

export const useCarrierDirectory = (scope) => {
    const { admin_clients } = useContext(UserContext);

    const { data: onwardData } = useQuery(QUERY_CLIENTS, { skip: scope.kind !== 'onward' });

    // Org's shipper client_ids drive the partnership-based query for Network Carriers
    // (carriers connected to the org's shippers via pricing_overrides — not in admin_clients).
    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.SHIPPER).map((c) => c.client_id),
        [admin_clients]
    );

    const { data: corpPricingData } = useQuery(GET_CORP_PRICING_OVERRIDES, {
        variables: { orgShipperIds },
        skip: scope.kind !== 'corp' || orgShipperIds.length === 0,
    });

    const allCarriers = useMemo(() => {
        if (scope.kind === 'corp') {
            // Flex Centers — org-internal carriers (admin_clients with role=CARRIER).
            const flex = (admin_clients || []).filter((c) => !!c.user?.roles?.CARRIER);
            const orgIds = new Set((admin_clients || []).map((c) => c.client_id));
            const byId = new Map(flex.map((c) => [c.client_id, c]));

            // Network Carriers — external carriers connected to org shippers via
            // pricing_overrides. They live in client / partner_client / assign on each
            // override; collect unique non-org carriers across all three slots.
            (corpPricingData?.pricing_overrides || []).forEach((p) => {
                [p.client, p.partner_client, p.assign].forEach((c) => {
                    if (!c) return;
                    const roles = c.user?.roles || {};
                    if ((!roles.CARRIER && !roles.MIDDLE_MILE_CARRIER) || roles.SHIPPER) return;
                    if (orgIds.has(c.client_id)) return;
                    if (!byId.has(c.client_id)) byId.set(c.client_id, c);
                });
            });

            return Array.from(byId.values());
        }
        if (scope.kind === 'onward') {
            // Onward returns carriers + mm_carriers as separate role-keyed lists. Dedupe by client_id;
            // when a client appears in both, the `carriers` entry wins because it carries the richer
            // payload (locations), which the WH slot needs.
            const carriers = onwardData?.carriers || [];
            const mm = onwardData?.mm_carriers || [];
            const byId = new Map();
            [...mm, ...carriers].forEach((c) => byId.set(c.client_id, c));
            return Array.from(byId.values());
        }
        return [];
    }, [scope.kind, admin_clients, onwardData, corpPricingData]);

    const carriersForLeg = useMemo(() => (slot) => filterCarriersByLeg(slot, allCarriers), [allCarriers]);

    return { allCarriers, carriersForLeg };
};

const QUERY_ONWARD_SHIPPERS = gql`
    query GetOnwardAdminShippers {
        results: clients(
            where: {
                user: { roles: { _has_key: "SHIPPER" } }
                approved: { _eq: true }
                deactivated: { _eq: false }
                test_acc: { _eq: false }
            }
            order_by: { business_name: asc_nulls_last }
        ) {
            client_id
            business_name
            shipper_type
        }
    }
`;

const isEligibleShipper = (c) =>
    !!c.user?.roles?.SHIPPER && c.approved === true && c.deactivated === false && c.test_acc === false;

// Identity key for a delivery_profile_assignments row under its unique
// constraint (shipper_id, order_type) — used to upsert rows into the
// assignments query cache after applying. zip_type is a mutable attribute of
// the slot, not part of its identity, so it must not be in the key: including
// it would strand a stale row when a re-apply overwrites the zip.
const assignmentKey = (a) => `${a.shipper_id}|${a.order_type}`;

export const useShipperDirectory = (scope, { onError } = {}) => {
    const { admin_clients } = useContext(UserContext);

    const { data: onwardShippers } = useQuery(QUERY_ONWARD_SHIPPERS, {
        skip: scope.kind !== 'onward',
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load shippers');
        },
    });

    const shippers = useMemo(() => {
        if (scope.kind === 'corp') {
            return (admin_clients || []).filter(isEligibleShipper).filter((c) => c.shipper_type === 'AUTO_ASSIGN');
        }
        if (scope.kind === 'onward') {
            return onwardShippers?.results || [];
        }
        return [];
    }, [scope.kind, admin_clients, onwardShippers]);

    // Assignments for every profile in scope (onward: org-less profiles; corp:
    // this org's profiles), driving the "applied" badge per shipper.
    const assignmentsWhere = whereForScope(scope);
    const { data: assignmentsData } = useQuery(QUERY_DELIVERY_PROFILE_ASSIGNMENTS, {
        variables: { where: { delivery_profile: assignmentsWhere || {} } },
        skip: !assignmentsWhere || (scope.kind === 'corp' && !scope.org_id),
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load profile assignments');
        },
    });

    const assignmentsByShipper = useMemo(() => {
        return (assignmentsData?.results || []).reduce((acc, a) => {
            (acc[a.shipper_id] = acc[a.shipper_id] || []).push(a);
            return acc;
        }, {});
    }, [assignmentsData]);

    const [applyMutation] = useMutation(APPLY_DELIVERY_PROFILE_ASSIGNMENTS, {
        onError: (e) => {
            captureException(e);
            onError?.('Failed to apply profile to shipper');
        },
        update: (cache, { data: { result } }) => {
            const returned = result?.returning || [];
            if (!returned.length) return;
            const variables = { where: { delivery_profile: assignmentsWhere || {} } };
            cache.updateQuery({ query: QUERY_DELIVERY_PROFILE_ASSIGNMENTS, variables }, (data) => {
                const list = data?.results || [];
                const byKey = new Map(list.map((a) => [assignmentKey(a), a]));
                returned.forEach((a) => byKey.set(assignmentKey(a), a));
                return { ...(data || {}), results: Array.from(byKey.values()) };
            });
        },
    });

    const applyToShipper = useCallback(
        (shipper_id, deliveryProfile, orderTypes, zipType) => {
            const assignments = buildAssignmentRows({ deliveryProfile, shipperId: shipper_id, zipType, orderTypes });
            if (!assignments.length) return Promise.resolve();
            return applyMutation({ variables: { assignments } });
        },
        [applyMutation]
    );

    return { shippers, assignmentsByShipper, applyToShipper };
};

export const useSubregionImportExport = ({
    subregions,
    deliveryProfile,
    deliveryProfileId,
    isOnwardAdmin,
    carriers,
    uploadSubregions,
    onError,
}) => {
    const COLUMNS = useColumns({ isOnwardAdmin });

    const importSubregions = useCallback(
        (headers, body) => {
            try {
                const { to_add, to_remove } = parseImportedSubregions(
                    headers,
                    body,
                    {
                        deliveryProfileId,
                        isOnwardAdmin,
                        carriersById: carriers.carriersById,
                        mmCarriersById: carriers.mmCarriersById,
                        warehouseCarriersById: carriers.warehouseCarriersById,
                    },
                    subregions
                );
                uploadSubregions(to_add, to_remove);
            } catch (e) {
                captureException(e);
                onError?.(`Error importing subregions: ${e.message}`);
            }
        },
        [subregions, deliveryProfileId, isOnwardAdmin, carriers, uploadSubregions, onError]
    );

    const exportSubregions = useCallback(() => {
        const rows = serializeSubregionsForExport(subregions, {
            genName: (s, i) => s.name || `Subregion ${i + 1}`,
        });
        const enrichments = {
            carriersById: carriers.carriersById,
            mmCarriersById: carriers.mmCarriersById,
            warehouseCarriersById: carriers.warehouseCarriersById,
        };
        csvDownload({
            headers: COLUMNS.map((col) => col.header),
            data: rows.map((row) => COLUMNS.map((col) => col.value(row, enrichments))),
            filename: `${deliveryProfile?.name}-${format(new Date(), 'yyyy-MM-dd')}.csv`,
            delimiter: ',',
        });
    }, [subregions, deliveryProfile, carriers, COLUMNS]);

    return { importSubregions, exportSubregions };
};
